import type { Condition, Data, Rule } from 'Campaign-Types';

export class RulesEngine {
  private data: Data = {};

  constructor(initialData?: Data) {
    if (initialData) {
      this.setData(initialData);
    }
  }

  setData(data: Data): void {
    this.data = { ...data };
  }

  evaluate(condition: Condition): boolean {
    return this.evaluateCondition(condition, this.data);
  }

  private evaluateCondition(condition: Condition, data: Data): boolean {
    if (condition.all) {
      return condition.all.every((ruleOrCondition) => {
        if (this.isRule(ruleOrCondition)) {
          return this.evaluateRule(ruleOrCondition, data);
        }
        return this.evaluateCondition(ruleOrCondition, data);
      });
    } else if (condition.any) {
      return condition.any.some((ruleOrCondition) => {
        if (this.isRule(ruleOrCondition)) {
          return this.evaluateRule(ruleOrCondition, data);
        }
        return this.evaluateCondition(ruleOrCondition, data);
      });
    } else {
      return false;
    }
  }

  private evaluateRule(rule: Rule, data: Data): boolean {
    const dataValue = data[rule.key];

    if (dataValue === undefined || dataValue === null) {
      return false;
    }

    switch (rule.operator) {
      case 'equal':
        return dataValue === rule.value;
      case 'notEqual':
        return dataValue !== rule.value;
      case 'greaterThan':
        return Number(dataValue) > Number(rule.value);
      case 'lessThan':
        return Number(dataValue) < Number(rule.value);
      case 'greaterThanOrEqual':
        return Number(dataValue) >= Number(rule.value);
      case 'lessThanOrEqual':
        return Number(dataValue) <= Number(rule.value);
      case 'contains':
        if (typeof dataValue === 'string' && typeof rule.value === 'string') {
          return dataValue.includes(rule.value);
        }
        return false;
      case 'notContains':
        if (typeof dataValue === 'string' && typeof rule.value === 'string') {
          return !dataValue.includes(rule.value);
        }
        return false;
      default:
        return false;
    }
  }

  private isRule(ruleOrCondition: Condition | Rule): ruleOrCondition is Rule {
    return (ruleOrCondition as Rule).key !== undefined;
  }
}
